import { InlineCodeHighlight } from '@mantine/code-highlight';
import {
  Button,
  Popover,
  SegmentedControl,
  TextInput,
  Tooltip,
  Textarea,
  Select,
  Badge,
  ActionIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useClipboard, useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconCircles,
  IconCopy,
  IconMail,
  IconPencil,
  IconSearch,
  IconSparkles,
  IconVariable,
  IconBolt,
  IconBrain,
  IconShield,
  IconSettings,
} from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { isMultitabTestAtom } from '~/atoms/no-code-atoms';
import { destructEmail } from '~/common/stably-email/destruct-email';
import { emailUsernameFromOrg } from '~/common/stably-email/email-username-from-org';
import type { ValidationResult } from '~/common/variable/validation';
import { zVariableValidationType, type VariableType } from '~/common/variable/validation';
import { VariableNameInput } from '~/components/common/VariableNameInput';
import { env } from '~/env.mjs';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useTestVariables } from '~/hooks/test/useTestVariables';
import { api } from '~/trpc-extra/api';
import type { OperationParentContext, ValueType } from '~/server/logic/steps/operation/types';
import { useGetInboxAddress } from '../../../newStep/useGetInboxAddress';
import OperationTabs from '../../OperationTabs';
import { ParameterizedInput } from '../../ParameterizedInput';
import type { DefineVariablePart } from '../converters';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import { VariableNameSuggestion } from './VariableNameSuggestion';
import clsx from 'clsx';
import type { AIExtractionModel, AIExtractionMethod } from '@prisma/client';
import { isValidExtractionMethodModelCombination } from '~/server/logic/steps/operation/types';

// Extraction model options
const EXTRACTION_MODEL_OPTIONS = {
  STABLY_FLASH: {
    label: 'Stably Flash',
    icon: IconBolt,
    description: 'Fast and recommended for simple extractions',
  } as const,
  STABLY_DEEP_THINK: {
    label: 'Stably Deep Think',
    icon: IconBrain,
    description: 'Recommended for complex extractions',
  } as const,
  BACKUP_MODEL: {
    label: 'Stably Deep Think (Backup)',
    icon: IconShield,
    description: 'Fallback model for edge cases',
  } as const,
} as const;

// Extraction method options
const EXTRACTION_METHOD_OPTIONS = {
  ARIA_SNAPSHOT: {
    label: 'Aria Snapshot (Page Structure)',
    description: 'Recommended for text, links, headings, form fields',
  } as const,
  SCREENSHOT: {
    label: 'Screenshot (Visual)',
    description: 'For colors, images, visual layout',
  } as const,
  SCREENSHOT_AND_ARIA: {
    label: 'Screenshot + Aria Snapshot (Both)',
    description: 'Most comprehensive, but slower',
  } as const,
} as const;

const isExtractionModel = (value: string): value is keyof typeof EXTRACTION_MODEL_OPTIONS => {
  return value in EXTRACTION_MODEL_OPTIONS;
};

const isExtractionMethod = (value: string): value is keyof typeof EXTRACTION_METHOD_OPTIONS => {
  return value in EXTRACTION_METHOD_OPTIONS;
};

const renderExtractionModelOption = ({ option }: { option: { value: string; label: string } }) => {
  const modelOption = isExtractionModel(option.value) ? EXTRACTION_MODEL_OPTIONS[option.value] : undefined;
  return (
    <div>
      <div className="flex items-center gap-2">
        {!!modelOption && <modelOption.icon size={14} />}
        <span className="text-xs">{option.label}</span>
      </div>
      <span className="text-[12px] text-gray-500">{modelOption?.description}</span>
    </div>
  );
};

export default function DefineVariablePart({
  testContentId,
  operation,
  operationId,
  part: { name, value, valueType, extractionModel: partExtractionModel, extractionMethod: partExtractionMethod },
  pageAlias,
  onClick,
  toggleEditing,
  readonly,
  updateFunc,
  parentContexts,
}: {
  testContentId: string;
  operation: SelectedOperation;
  operationId: string;
  part: DefineVariablePart;
  pageAlias?: string;
  onClick?: () => void;
  // For Operation wide editing
  toggleEditing: (editing: boolean) => void;
  readonly?: boolean;
  updateFunc: (part: Omit<DefineVariablePart, 'type'>) => Promise<void>;
  parentContexts?: OperationParentContext[];
}) {
  const { activeOrg } = useAuthData();
  const testVariables = useTestVariables({
    testContentId,
    limit: { type: 'operationId', id: operationId },
    parentContexts,
  });
  const hasMultipleTabs = useAtomValue(isMultitabTestAtom);
  const { mutateAsync: validateAndSuggestVariableName, isPending: isValidatingVariableName } =
    api.operation.validateAndSuggestVariableName.useMutation({
      onError: (error) => {
        console.error('Error validating/suggesting variable name:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to validate/suggest variable name',
          color: 'red',
        });
      },
    });

  const latestValidationRequestTimeRef = useRef(0);

  const emailOrgPrefix = useMemo(() => emailUsernameFromOrg(activeOrg?.orgName || ''), [activeOrg?.orgName]);
  const [variableType, setVariableType] = useState<VariableType>(() => {
    const { orgName, domain } = destructEmail(value || '');
    const newVariableType: VariableType =
      emailOrgPrefix === orgName && domain === env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN ? 'EMAIL_INBOX' : 'NORMAL';
    return newVariableType;
  });
  const { getInboxAddressDynamic } = useGetInboxAddress();
  const clipboard = useClipboard({ timeout: 500 });

  const [editing, setEditing] = useState(false);

  const [suggestionState, setSuggestionState] = useState<{
    suggestedVariableName?: string;
    validationReason?: string;
    lastValidatedParams?: { variableDescription: string; variableName: string; suggestedName?: string };
  }>({});

  const [hasOverlayOpen, setHasOverlayOpen] = useState(false);

  // Model selection for AI extraction
  // Default to ARIA_SNAPSHOT method with STABLY_FLASH model (works with all extraction methods)
  const [extractionMethod, setExtractionMethod] = useState<AIExtractionMethod>(partExtractionMethod ?? 'ARIA_SNAPSHOT');
  const [extractionModel, setExtractionModel] = useState<AIExtractionModel>(partExtractionModel ?? 'STABLY_FLASH');

  const form = useForm<{
    name: string;
    value: string;
    valueType: ValueType;
    pageAlias?: string;
  }>({
    initialValues: {
      name: name || '',
      value: value || '',
      valueType: valueType || 'STATIC',
      pageAlias: pageAlias || 'page',
    },
    validate: {
      name: (variableName) => {
        const result = zVariableValidationType.safeParse(variableName);
        if (!result.success) {
          return result.error.message || 'Invalid variable name';
        }

        // Check if variable name is already used by another variable
        const existingVariables = (testVariables || []).map((v) => v.name).filter((varName) => varName !== name);
        if (existingVariables.includes(variableName)) {
          return 'Variable name is already being used';
        }

        return undefined;
      },
      // Value is a misnomer. It should be 'definition'
      value: (value, { valueType }) => {
        if (!value) {
          return 'Value required';
        }
        if (variableType === 'EMAIL_INBOX' && valueType === 'STATIC') {
          const result = z.string().email().safeParse(value);
          if (!result.success) {
            // Check for common issues and provide specific guidance
            if (value.includes(' ')) {
              return 'Spaces are not allowed in email addresses. Use dashes (-) or underscores (_) instead.';
            }
            return 'Invalid email address';
          }
        }
        return null;
      },
    },
  });

  const debounceDelay = 800;
  const [debouncedDescription] = useDebouncedValue(form.values.value, debounceDelay);
  const [debouncedVariableName] = useDebouncedValue(form.values.name, debounceDelay);

  // Generate suggested variable name when debounced values change
  useEffect(() => {
    if (!debouncedDescription || !testVariables || !validateAndSuggestVariableName) {
      setSuggestionState({});
      return;
    }

    const shouldSkipValidation =
      suggestionState.lastValidatedParams &&
      suggestionState.lastValidatedParams.variableDescription === debouncedDescription &&
      suggestionState.lastValidatedParams.suggestedName === debouncedVariableName;

    if (shouldSkipValidation) {
      return;
    }

    if (!form.isDirty()) {
      return;
    }

    const currentRequestTime = Date.now();
    latestValidationRequestTimeRef.current = currentRequestTime;

    validateAndSuggestVariableName({
      description: debouncedDescription,
      existingVariables: testVariables.map((v) => v.name).filter((varName) => varName !== debouncedVariableName),
      existingVariableName: debouncedVariableName,
    })
      .then((suggestion: ValidationResult) => {
        // there is a potential race condition here, we need to ensure we only update the UI if the request is still the latest one
        if (currentRequestTime !== latestValidationRequestTimeRef.current) {
          return;
        }

        // the API validates the current variable name:
        // 1. If isValid is true, the frontend does nothing.
        // 2. If isValid is false, the API returns a suggestion, and we update the UI to display it.
        if (!suggestion.isValid) {
          setSuggestionState({
            suggestedVariableName: suggestion.suggestedVariableName,
            validationReason: suggestion.reason,
            lastValidatedParams: {
              variableDescription: debouncedDescription,
              variableName: debouncedVariableName,
              suggestedName: suggestion.suggestedVariableName,
            },
          });
        } else {
          setSuggestionState({});
        }
      })
      .catch(() => {
        if (currentRequestTime === latestValidationRequestTimeRef.current) {
          setSuggestionState({});
        }
      });
  }, [debouncedDescription, debouncedVariableName]);

  const actionMode = form.values.valueType === 'AI_EXTRACT_PROMPT' ? 'EXTRACT' : 'DEFINE';

  const handleFormSubmission = useCallback(
    async ({
      name,
      value,
      valueType,
      pageAlias,
    }: {
      name: string;
      value: string;
      valueType: ValueType;
      pageAlias?: string;
    }) => {
      const response = { value, valueType, pageAlias };

      toggleEditing(!editing);
      setEditing(!editing);
      await updateFunc({
        name,
        ...response,
        includeScreenshot: actionMode === 'EXTRACT',
        extractionModel: actionMode === 'EXTRACT' ? extractionModel : undefined,
        extractionMethod: actionMode === 'EXTRACT' ? extractionMethod : undefined,
      });
    },
    [updateFunc, editing, actionMode, extractionModel, extractionMethod],
  );

  const handleClosePopover = useCallback(() => {
    toggleEditing(false);
    setEditing(false);
    form.setFieldValue('value', value);
    form.setFieldValue('valueType', valueType);
  }, [toggleEditing, setEditing, value, valueType]);

  return (
    <Popover
      width={400}
      position="bottom"
      withArrow
      shadow="md"
      opened={editing}
      closeOnClickOutside={!hasOverlayOpen} // We don't want to close the popover an overlay is open above this (eg. the variable guide modal)
      onClose={handleClosePopover}
      onDismiss={handleClosePopover}
    >
      <Popover.Target>
        <div
          className="break-all rounded-md"
          onDoubleClick={() => {
            onClick?.();
            toggleEditing(true);
            setEditing(true);
          }}
        >
          {actionMode === 'EXTRACT' && (
            <div className="mb-2 flex w-full items-center gap-2">
              <Badge
                leftSection={<IconSearch size={14} />}
                color="grape"
                variant="filled"
                className="bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 dark:hover:bg-purple-900/30 dark:hover:text-purple-300"
                classNames={{ root: 'normal-case' }}
              >
                Extract
              </Badge>
              <ActionIcon
                size="xs"
                radius="xl"
                variant="transparent"
                color="gray"
                className="hover:bg-gray-100 hover:dark:bg-gray-800"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick?.();
                  toggleEditing(true);
                  setEditing(true);
                }}
              >
                <IconSettings />
              </ActionIcon>
            </div>
          )}
          {variableType === 'EMAIL_INBOX' && (
            <Badge
              variant="default"
              leftSection={<IconMail size={12} />}
              className="mr-0.5 rounded-md text-xs font-medium normal-case"
            >
              Email
            </Badge>
          )}
          {'  '}
          Define{' '}
          <InlineCodeHighlight
            code={`VARS.${name}`}
            language="tsx"
          />{' '}
          as{' '}
          {actionMode === 'EXTRACT' ?
            <>
              <span className="flex items-center">
                <IconSparkles
                  size={16}
                  className="text-white-500 mr-1 flex-shrink-0"
                />
                <span className="text-white-900 dark:text-white-600 hover:cursor-pointer">{value}</span>
              </span>
              {actionMode === 'EXTRACT' && (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-start gap-1">
                    <div className="text-blue-700 dark:text-blue-300">extracted from</div>
                    <OperationTabs
                      isForDisplayingOpensPageAlias={false}
                      operation={operation}
                      testContentId={testContentId}
                      readonly={readonly}
                      showTabLabel={true}
                    />
                  </div>
                  {!!partExtractionMethod && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <IconSettings size={12} />
                      <span>{EXTRACTION_METHOD_OPTIONS[partExtractionMethod]?.label ?? partExtractionMethod}</span>
                    </div>
                  )}
                </div>
              )}
            </>
          : valueType === 'STATIC' ?
            <span className="text-yellow-900 hover:cursor-pointer dark:text-yellow-600">{value}</span>
          : <InlineCodeHighlight
              code={value}
              language="tsx"
            />
          }
        </div>
      </Popover.Target>
      <Popover.Dropdown
        className="dark:bg-neutral-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col">
          <form
            className="mb-2 flex flex-col gap-2"
            onSubmit={form.onSubmit(handleFormSubmission)}
          >
            {/* Mode selector */}
            <SegmentedControl
              size="xs"
              className="mt-2"
              value={actionMode}
              disabled={readonly}
              onChange={(value) => {
                if (value === 'EXTRACT') {
                  form.setFieldValue('valueType', 'AI_EXTRACT_PROMPT');
                } else {
                  form.setFieldValue('valueType', 'STATIC');
                }
              }}
              data={[
                {
                  value: 'DEFINE',
                  label: (
                    <div className="flex items-center justify-center gap-1">
                      <Tooltip
                        label="Define a variable with a value"
                        withArrow
                      >
                        <IconPencil size={18} />
                      </Tooltip>
                      <span className="text-sm font-medium">Define</span>
                    </div>
                  ),
                },
                {
                  value: 'EXTRACT',
                  label: (
                    <div className="flex items-center justify-center gap-1">
                      <Tooltip
                        label="Extract visual content from the page with AI"
                        withArrow
                      >
                        <IconSearch size={18} />
                      </Tooltip>
                      <span className="text-sm font-medium">Extract</span>
                    </div>
                  ),
                },
              ]}
            />
            {/* Variable Name row */}
            <div>
              <div className="mb-1 flex items-center gap-2">
                <span className="text-sm font-medium">Variable name</span>
                <VariableNameSuggestion
                  suggestedVariableName={suggestionState.suggestedVariableName}
                  validationReason={suggestionState.validationReason}
                  currentVariableName={form.values.name}
                  onApplySuggestion={(name) => form.setFieldValue('name', name)}
                  isValidating={isValidatingVariableName}
                />
                <SegmentedControl
                  size="xs"
                  className={clsx('ml-auto', { invisible: actionMode === 'EXTRACT' })}
                  value={variableType}
                  onChange={(value) => {
                    if (value === 'NORMAL' || value === 'EMAIL_INBOX') {
                      setVariableType(value);
                      // TODO: AI_GENERATE_PROMPT is not supported yet, we will enable this when we support it
                      // if (form.values.valueType === 'AI_GENERATE_PROMPT') {
                      //     form.setFieldValue('valueType', 'STATIC');
                      // }
                    }
                  }}
                  data={[
                    {
                      value: 'NORMAL',
                      label: (
                        <Tooltip
                          label="Suitable for most use cases"
                          withArrow
                        >
                          <IconVariable size={18} />
                        </Tooltip>
                      ),
                    },
                    {
                      value: 'EMAIL_INBOX',
                      label: (
                        <Tooltip
                          label="Stably can receive emails on your behalf"
                          withArrow
                        >
                          <IconMail size={18} />
                        </Tooltip>
                      ),
                    },
                  ]}
                />
              </div>
              <VariableNameInput
                autoFocus
                placeholder=""
                readOnly={readonly}
                value={form.values.name}
                onChange={(newValue) => form.setFieldValue('name', newValue)}
                error={form.getInputProps('name').error}
                className="w-full"
                label=""
              />
            </div>
            {actionMode === 'EXTRACT' ?
              <Textarea
                label="Extraction prompt"
                labelProps={{ className: 'my-2 text-sm' }}
                placeholder="Provide an instruction for the AI to extract visual content from the page"
                readOnly={readonly}
                value={form.values.value || ''}
                autosize
                minRows={2}
                maxRows={4}
                error={form.getInputProps('value').error}
                onChange={(event) => form.setFieldValue('value', event.currentTarget.value)}
                classNames={{
                  input:
                    'border-blue-500/50 bg-blue-500/10 text-blue-500 placeholder-blue-500/60 focus:border-blue-500 dark:border-blue-300/50 dark:bg-blue-900/20 dark:text-blue-300 dark:placeholder-blue-300/50 dark:focus:border-blue-300 animate-slowFadeIn text-xs',
                }}
              />
            : <ParameterizedInput
                label="Variable value"
                // TODO: AI_GENERATE_PROMPT is not supported yet, we will enable this when we support it
                // placeholder={
                //   form.values.valueType === 'AI_GENERATE_PROMPT' ?
                //     'Generate a value using AI'
                //   : 'Enter a variable value here'
                // }
                placeholder="Enter a variable value here"
                disabled={readonly}
                value={form.values.value || ''}
                valueType={form.values.valueType || 'STATIC'}
                error={form.getInputProps('value').error}
                onValueChange={(x: string) => form.setFieldValue('value', x)}
                onValueTypeChange={(x: ValueType) => form.setFieldValue('valueType', x)}
                variables={testVariables}
                variableType={variableType}
                onOverlayStateChange={setHasOverlayOpen}
              />
            }

            {/* Extraction method dropdown */}
            {actionMode === 'EXTRACT' && (
              <Select
                label="Extraction Method"
                description="Choose how to extract data from the page"
                data={Object.entries(EXTRACTION_METHOD_OPTIONS).map(([key, opt]) => ({
                  value: key,
                  label: opt.label,
                }))}
                value={extractionMethod}
                disabled={readonly}
                comboboxProps={{ withinPortal: false }}
                renderOption={({ option }) => {
                  const methodOption =
                    isExtractionMethod(option.value) ? EXTRACTION_METHOD_OPTIONS[option.value] : undefined;
                  return (
                    <div>
                      <div className="text-xs font-medium">{option.label}</div>
                      <div className="text-[11px] text-gray-500">{methodOption?.description}</div>
                    </div>
                  );
                }}
                allowDeselect={false}
                onOptionSubmit={(selectedValue) => {
                  // Type guard to ensure selectedValue is a valid AIExtractionMethod
                  if (isExtractionMethod(selectedValue)) {
                    setExtractionMethod(selectedValue);
                    // If the new method is not compatible with current model, reset to STABLY_FLASH
                    // Use functional update to avoid race conditions
                    setExtractionModel((currentModel) => {
                      if (!isValidExtractionMethodModelCombination(selectedValue, currentModel)) {
                        return 'STABLY_FLASH';
                      }
                      return currentModel;
                    });
                  }
                }}
              />
            )}

            {/* Extraction model dropdown */}
            {actionMode === 'EXTRACT' && (
              <Select
                label="Model"
                data={Object.entries(EXTRACTION_MODEL_OPTIONS)
                  .filter(([key]) => {
                    // Filter out models that aren't compatible with current extraction method
                    if (isExtractionModel(key)) {
                      return isValidExtractionMethodModelCombination(extractionMethod, key);
                    }
                    return false;
                  })
                  .map(([key, opt]) => ({
                    value: key,
                    label: opt.label,
                  }))}
                value={extractionModel}
                disabled={readonly}
                // oxlint-disable-next-line react/no-unstable-nested-components
                leftSection={(() => {
                  const modelConfig =
                    isExtractionModel(extractionModel) ? EXTRACTION_MODEL_OPTIONS[extractionModel] : undefined;
                  const SelectedIcon = modelConfig?.icon;
                  return SelectedIcon ? <SelectedIcon size={14} /> : null;
                })()}
                comboboxProps={{ withinPortal: false }}
                renderOption={renderExtractionModelOption}
                allowDeselect={false}
                onOptionSubmit={(selectedValue) => {
                  // Type guard to ensure selectedValue is a valid AIExtractionModel
                  if (isExtractionModel(selectedValue)) {
                    setExtractionModel(selectedValue);
                  }
                }}
              />
            )}

            {actionMode === 'EXTRACT' && !!hasMultipleTabs && (
              <div className="mt-2">
                <div className="mb-1 text-xs font-medium">Extract from</div>
                <TextInput
                  placeholder="page"
                  value={form.values.pageAlias || ''}
                  onChange={(e) => form.setFieldValue('pageAlias', e.target.value)}
                  readOnly={readonly}
                  className="w-full"
                  leftSection={<div className="whitespace-nowrap text-sm font-medium">Tab:</div>}
                  leftSectionWidth={50}
                  classNames={{ label: 'hidden' }}
                />
              </div>
            )}

            {variableType === 'EMAIL_INBOX' && (
              <div className="mt-1 flex justify-between">
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconCircles size={12} />}
                  onClick={() => {
                    const dynamicValue = getInboxAddressDynamic('Date.now()');
                    form.setFieldValue('valueType', 'DYNAMIC');
                    form.setFieldValue('value', dynamicValue);
                  }}
                >
                  Use dynamic inbox address
                </Button>
                {form.values.valueType === 'STATIC' && (
                  <Button
                    variant="subtle"
                    size="compact-xs"
                    leftSection={<IconCopy size={12} />}
                    color={clipboard.copied ? 'teal' : 'dark'}
                    onClick={() => {
                      clipboard.copy(form.values.value);
                      notifications.show({
                        title: 'Email address copied',
                        message: 'You can now paste it into the email inbox step',
                        color: 'teal',
                      });
                    }}
                  >
                    Copy address
                  </Button>
                )}
              </div>
            )}
            {!readonly && (
              <div className="flex justify-end gap-2 py-0">
                <Button
                  variant="subtle"
                  onClick={handleClosePopover}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={readonly}
                >
                  Save
                </Button>
              </div>
            )}
          </form>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
